import { graphqlRequest } from 'utils/graphqlAPI';
import { gql } from 'utils/graphql';
import {
  CreateChatStoreOrderInput,
  CreateChatStoreOrderMutation,
  CreateStoreAndAgreementRequestInput,
  CreateStoreAndAgreementResult,
  GetChatOrderEligibilityQuery,
  DeliveryCompany,
  OrderSheetByIdQuery,
} from '__generated__/commerce';

const CREATE_CHAT_STORE_ORDER = gql`
  mutation CreateChatStoreOrder($input: CreateChatStoreOrderInput!) {
    createChatStoreOrder(input: $input) {
      chatStoreOrder {
        storeOrder {
          id
          status
          name
        }
      }
      error {
        ... on ChatOrderInvalidOrder { message }
        ... on ChatOrderStoreNotFound { message }
        ... on ChatOrderStoreNotOwned { message }
      }
    }
  }
`;

const ORDER_SHEET_BY_ID = gql`
  query OrderSheetById($storeOrderId: ID!) {
    orderSheet(orderSheetId: $storeOrderId) {
      deliveryTracking {
        invoice
      }
    }
  }
`;

const BUSINESS_ACCOUNT_CHAT_ORDER_FIELDS = gql`
  fragment BusinessAccountChatOrderFields on BusinessAccount {
    businessRegistration {
      inspectionStatus
    }
    settlement {
      accountNumber
    }
    status
    saleMethodSetting {
      chatOrderEnabled
    }
    isChatOrderTarget
  }
`;

const GET_CHAT_ORDER_ELIGIBILITY = gql`
  query GetChatOrderEligibility($baId: ID!) {
    baById(baId: $baId) {
      ...BusinessAccountChatOrderFields
    }
    store(baId: $baId) {
      _id
      mostRecentChatOrderPosts {
        businessPostId
        title
        products {
          _id
          name
          deliveryFeeOptions {
            name
            fee {
              amount
            }
          }
          price {
            amount
            currency
          }
          thumbnail {
            _id
            thumbnail
            url
          }
          options {
            _id
            name
            price {
              amount
              currency
            }
          }
        }
      }
    }
  }
  ${BUSINESS_ACCOUNT_CHAT_ORDER_FIELDS}
`;

const CREATE_STORE_AND_AGREEMENT = gql`
  mutation CreateStoreAndAgreement($input: CreateStoreAndAgreementRequestInput!) {
    createStoreAndAgreement(input: $input) {
      store {
        id
        _id
        businessAccount {
          ...BusinessAccountChatOrderFields
        }
      }
      isNewStore
      error {
        ... on BusinessAccountNotFound { message }
        ... on InvalidSaleMethodSetting { message }
        ... on TermsNotAgreed { message }
      }
    }
  }
  ${BUSINESS_ACCOUNT_CHAT_ORDER_FIELDS}
`;

export const createChatStoreOrder = async (input: CreateChatStoreOrderInput) => {
  const response = await graphqlRequest<CreateChatStoreOrderMutation>(
    CREATE_CHAT_STORE_ORDER,
    { input }
  );
  return response.createChatStoreOrder;
};

export const getChatOrderEligibility = async (baId: string) => {
  const response = await graphqlRequest<GetChatOrderEligibilityQuery>(
    GET_CHAT_ORDER_ELIGIBILITY,
    { baId }
  );

  return response;
};



export const createStoreAndAgreement = async (input: CreateStoreAndAgreementRequestInput) => {
  const response = await graphqlRequest<{ createStoreAndAgreement: CreateStoreAndAgreementResult }>(
    CREATE_STORE_AND_AGREEMENT,
    { input }
  );
  return response.createStoreAndAgreement;
};

export const getOrderSheetById = async (storeOrderId: string) => {
  const response = await graphqlRequest<OrderSheetByIdQuery>(
    ORDER_SHEET_BY_ID,
    { storeOrderId }
  );
  return response.orderSheet;
};

const START_DELIVERY_V2 = gql`
  mutation StartDeliveryV2($storeOrderId: ID!, $company: DeliveryCompany, $invoice: String) {
    startDeliveryV2(storeOrderId: $storeOrderId, company: $company, invoice: $invoice) {
      orderSheet {
        body {
          status
          deliveryInfo {
            status
          }
        }
      }
      error {
        __typename
        ... on StartDeliveryInvalidDeliveryStatusError {
          deliveryStatus
        }
        ... on StartDeliveryInvalidInvoiceError {
          company
          invoice
        }
        ... on StartDeliveryInvalidOrderStatusError {
          orderStatus
        }
      }
    }
  }
`;

interface StartDeliveryV2Response {
  startDeliveryV2: {
    orderSheet?: {
      body: {
        status: string;
        deliveryInfo: {
          status: string;
        };
      };
    };
    error?: {
      __typename: string;
      deliveryStatus?: string;
      company?: string;
      invoice?: string;
      orderStatus?: string;
    };
  };
}

export const startDeliveryV2 = async (
  storeOrderId: string,
  company: DeliveryCompany | null,
  invoice: string
) => {
  const response = await graphqlRequest<StartDeliveryV2Response>(
    START_DELIVERY_V2,
    { storeOrderId, company, invoice }
  );
  return response.startDeliveryV2;
};

const GET_HOIAN_USER = gql`
  query GetHoianUser($id: ID!) {
    hoianUserById(id: $id) {
      _id
      nickname
    }
  }
`;

interface GetHoianUserResponse {
  hoianUserById: {
    _id: string;
    nickname: string;
  } | null;
}

export const getHoianUser = async (id: string) => {
  const response = await graphqlRequest<GetHoianUserResponse>(
    GET_HOIAN_USER,
    { id }
  );
  return response.hoianUserById;
};
